import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import PermissionGuard from '../../components/PermissionGuard';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { userService, UserDTO } from '../../services/userService';
import '../../pages/Products/ProductCategoryListPage.css';
import { CirclePlus, RefreshCw, Eye, PenLine, UserX, UserCheck } from 'lucide-react';

const UserListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message || null
  );

  useEffect(() => {
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getUsers({ pageNumber: 1, pageSize: 100 });
      const data = response?.data?.data;
      if (Array.isArray(data)) {
        setUsers(data);
      }
      setLoading(false);
    } catch {
      setError(t('user.failedToLoad'));
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await userService.getUsers({ pageNumber: 1, pageSize: 100 });
      const data = response?.data?.data;
      if (Array.isArray(data)) {
        setUsers(data);
      }
    } catch {
      setError(t('user.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'email',
      label: t('user.email'),
      sortable: true,
      width: '220px',
      render: (value) => <code>{value}</code>,
    },
    {
      key: 'firstName',
      label: t('user.fullName'),
      sortable: true,
      width: '180px',
      render: (_value, item: UserDTO) => (
        <strong>{item.firstName} {item.lastName}</strong>
      ),
    },
    {
      key: 'lastLogin',
      label: t('user.lastLogin'),
      sortable: false,
      width: '140px',
      hideMobile: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : '—',
    },
    {
      key: 'roles',
      label: t('user.roles'),
      sortable: false,
      width: '200px',
      hideMobile: true,
      render: (_value, item: UserDTO) =>
        item.roles && item.roles.length > 0 ? (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {item.roles.map((role) => (
              <span key={role.id} className="badge badge-draft" style={{ fontSize: '11px' }}>
                {role.name}
              </span>
            ))}
          </span>
        ) : (
          <span style={{ color: '#9ca3af', fontSize: '13px' }}>{t('user.noRoles')}</span>
        ),
    },
    {
      key: 'isActive',
      label: t('user.status'),
      width: '100px',
      hideMobile: true,
      render: (value) => (
        <span className={`badge ${value ? 'badge-active' : 'badge-inactive'}`}>
          {value ? t('user.active') : t('user.inactive')}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={18} strokeWidth={2} />
      ),
      onClick: (item: UserDTO) => navigate(`/users/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    ...(hasPermission(Permissions.Users.Edit)
      ? [{
          icon: (
            <PenLine size={18} strokeWidth={2} />
          ),
          onClick: (item: UserDTO) => navigate(`/users/${item.id}/edit`),
          className: 'grid-action-btn-edit',
          title: t('common.edit'),
        }]
      : []),
    ...(hasPermission(Permissions.Users.Delete)
      ? [
          {
            icon: (
              <UserX size={18} strokeWidth={2} />
            ),
            onClick: (item: UserDTO) => navigate(`/users/${item.id}/deactivate`),
            className: 'grid-action-btn-delete',
            title: t('user.deactivateUser'),
            hidden: (item: UserDTO) => !item.isActive,
          },
          {
            icon: (
              <UserCheck size={18} strokeWidth={2} />
            ),
            onClick: (item: UserDTO) => navigate(`/users/${item.id}/deactivate`),
            className: 'grid-action-btn-submit',
            title: t('user.activateUser'),
            hidden: (item: UserDTO) => item.isActive,
          },
        ]
      : []),
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('user.title')}</h1>
          <div className="header-actions">
            <PermissionGuard claim={Permissions.Users.Create}>
              <button
                className="btn-new"
                onClick={() => navigate('/users/create')}
              >
                <CirclePlus/>
                {t('user.newUser')}
              </button>
            </PermissionGuard>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('user.refreshUsers')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('user.refreshUsers')}
            </button>
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            <strong>{t('common.success')}:</strong> {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={users}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('user.noUsersFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default UserListPage;
